import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { formatMinutesAsHM } from '../../utils/time';

interface Props {
  weekRangeString: string;
  /** Total worked time for the week, in exact minutes. */
  minutesTotal: number;
  /** Contractual weekly base (base_hours_per_week) in minutes — shown as the
   *  reference next to the worked total, with the delta. Omitted while the
   *  leave config is loading. */
  expectedMinutes?: number;
  isCurrentWeek: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
}

export const WeekSelectorCard: React.FC<Props> = ({
  weekRangeString, minutesTotal, expectedMinutes, isCurrentWeek, onPrevious, onNext, onToday,
}) => {
  const hasExpected = typeof expectedMinutes === 'number' && expectedMinutes > 0;
  const delta = hasExpected ? minutesTotal - expectedMinutes : 0;
  const deltaLabel = delta === 0 ? '' : `${delta > 0 ? '+' : '−'}${formatMinutesAsHM(Math.abs(delta))}`;
  return (
  <div className="pl-rd-card">
    {/* Row 1: navigation. Row 2: hours badge — keeping the (now wider)
        badge off the nav row stops it overlapping the week-range title. */}
    <div className="pl-rd-week-sel">
      <button className="pl-rd-arrow" onClick={onPrevious} aria-label="Semaine précédente">
        <ChevronLeftIcon className="w-5 h-5" />
      </button>
      <div className="pl-rd-week-range">{weekRangeString}</div>
      <button className="pl-rd-arrow" onClick={onNext} aria-label="Semaine suivante">
        <ChevronRightIcon className="w-5 h-5" />
      </button>
      <button className="pl-rd-today-btn" onClick={onToday} disabled={isCurrentWeek}>
        <CalendarIcon className="w-4 h-4" />
        Aujourd'hui
      </button>
    </div>
    <div className="pl-rd-week-meta">
      <span
        className="pl-rd-hours-badge"
        title={hasExpected
          ? `Heures planifiées cette semaine vs votre base hebdomadaire (${formatMinutesAsHM(expectedMinutes!)})`
          : 'Total des heures planifiées cette semaine'}
      >
        <ClockIcon className="w-4 h-4" />
        {formatMinutesAsHM(minutesTotal)}
        {hasExpected && (
          <span className="pl-rd-hours-expected"> / {formatMinutesAsHM(expectedMinutes!)}</span>
        )}
        {' '}travaillées
        {hasExpected && deltaLabel && (
          <span className={`pl-rd-hours-delta ${delta < 0 ? 'is-under' : 'is-over'}`}>{deltaLabel}</span>
        )}
      </span>
    </div>
  </div>
  );
};
